/**
 * Manter sincronizado com api/src/billing/plans/.
 */

export const SALONFLOW_FEATURES = {
  AGENDA: 'agenda',
  SERVICES: 'services',
  ATTENDANTS: 'attendants',
  CLIENTS: 'clients',
  DASHBOARD_BASIC: 'dashboard.basic',
  DASHBOARD_EXECUTIVE: 'dashboard.executive',
  DASHBOARD_SALES: 'dashboard.sales',
  DASHBOARD_STOCK: 'dashboard.stock',
  DASHBOARD_FINANCE: 'dashboard.finance',
  PUBLIC_PAGE: 'public_page',
  LANDING_PAGE_BASIC: 'landing_page.basic',
  LANDING_PAGE_FULL: 'landing_page.full',
  PLANS: 'plans',
  SUBSCRIPTIONS: 'subscriptions',
  SUBSCRIPTION_PERIODS: 'subscription_periods',
  RECURRENCES: 'recurrences',
  SALES: 'sales',
  WAITING_LIST: 'waiting_list',
  TIME_OFFERS: 'time_offers',
  REMINDERS: 'reminders',
  CLIENT_GROUPS: 'client_groups',
  WHATSAPP_OPERATIONAL: 'whatsapp.operational',
  GOOGLE_CALENDAR: 'google_calendar',
  STOCK: 'stock',
  FINANCE: 'finance',
  INSTAGRAM: 'instagram',
  NOTIFICATIONS: 'notifications',
} as const;

export type SalonFlowFeatureKey =
  (typeof SALONFLOW_FEATURES)[keyof typeof SALONFLOW_FEATURES];

export type SalonFlowPlanKey =
  | 'trial'
  | 'basic'
  | 'professional'
  | 'premium';

export const SALONFLOW_PLAN_LABEL: Record<SalonFlowPlanKey, string> = {
  trial: 'Teste gratuito',
  basic: 'Essencial',
  professional: 'Profissional',
  premium: 'Completo',
};

const BASIC_MODULES: ReadonlyArray<SalonFlowFeatureKey> = [
  SALONFLOW_FEATURES.AGENDA,
  SALONFLOW_FEATURES.SERVICES,
  SALONFLOW_FEATURES.ATTENDANTS,
  SALONFLOW_FEATURES.CLIENTS,
  SALONFLOW_FEATURES.DASHBOARD_BASIC,
  SALONFLOW_FEATURES.PUBLIC_PAGE,
  SALONFLOW_FEATURES.LANDING_PAGE_BASIC,
  SALONFLOW_FEATURES.NOTIFICATIONS,
];

const PROFESSIONAL_EXTRA: readonly SalonFlowFeatureKey[] = [
  SALONFLOW_FEATURES.DASHBOARD_SALES,
  SALONFLOW_FEATURES.PLANS,
  SALONFLOW_FEATURES.SUBSCRIPTIONS,
  SALONFLOW_FEATURES.SUBSCRIPTION_PERIODS,
  SALONFLOW_FEATURES.RECURRENCES,
  SALONFLOW_FEATURES.SALES,
  SALONFLOW_FEATURES.WAITING_LIST,
  SALONFLOW_FEATURES.TIME_OFFERS,
  SALONFLOW_FEATURES.CLIENT_GROUPS,
  SALONFLOW_FEATURES.WHATSAPP_OPERATIONAL,
  SALONFLOW_FEATURES.GOOGLE_CALENDAR,
  SALONFLOW_FEATURES.LANDING_PAGE_FULL,
];

/** Modulos efetivos por plano normalizado da sessao (`systemPlanKey`). */
export const SALONFLOW_PLAN_MODULES: Record<
  SalonFlowPlanKey,
  ReadonlyArray<SalonFlowFeatureKey> | readonly ['*']
> = {
  trial: BASIC_MODULES,
  basic: BASIC_MODULES,
  professional: [...BASIC_MODULES, ...PROFESSIONAL_EXTRA],
  premium: ['*'],
};

export function salonflowPlanHasFeature(
  planKey: string | undefined | null,
  feature: SalonFlowFeatureKey,
): boolean {
  const k = normalizePlanKey(planKey);
  const modules = SALONFLOW_PLAN_MODULES[k];
  if (modules[0] === '*') return true;
  return (modules as readonly SalonFlowFeatureKey[]).includes(feature);
}

/** Menor tier que inclui a feature (para texto de upgrade). */
export function getRequiredUpgradeForFeature(
  feature: SalonFlowFeatureKey,
): SalonFlowPlanKey | null {
  const ORDER: SalonFlowPlanKey[] = [
    'trial',
    'basic',
    'professional',
    'premium',
  ];
  for (const p of ORDER) {
    const mods = SALONFLOW_PLAN_MODULES[p];
    if (mods[0] === '*' || (mods as readonly SalonFlowFeatureKey[]).includes(feature)) {
      return p;
    }
  }
  return null;
}

export function normalizePlanKey(
  raw: string | undefined | null,
): SalonFlowPlanKey {
  const t = String(raw || '').trim();
  if (
    t === 'trial' ||
    t === 'basic' ||
    t === 'professional' ||
    t === 'premium'
  ) {
    return t;
  }
  return 'trial';
}

export type TrialRemaining = {
  days: number;
  ended: boolean;
};

export function getTrialRemainingDays(trialEndsAt?: string | Date | null): TrialRemaining | null {
  if (trialEndsAt === undefined || trialEndsAt === null || trialEndsAt === '') {
    return null;
  }
  const end = typeof trialEndsAt === 'string' ? new Date(trialEndsAt) : trialEndsAt;
  if (Number.isNaN(end.getTime())) {
    return null;
  }
  const now = Date.now();
  const diffMs = end.getTime() - now;
  const days = Math.ceil(diffMs / (1000 * 60 * 60 * 24));
  return {
    days: Math.max(0, days),
    ended: diffMs <= 0,
  };
}
